import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import type { UserRole } from '@/types/api';

interface ProtectedRouteProps {
  children: React.ReactNode;
  allowedRoles?: UserRole[];
  redirectTo?: string;
}

export function ProtectedRoute({
  children,
  allowedRoles,
  redirectTo = '/login',
}: ProtectedRouteProps) {
  const { isLoggedIn, isLoading, role } = useAuth();
  const location = useLocation();

  // Show nothing while checking auth status
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!isLoggedIn) {
    return <Navigate to={redirectTo} state={{ from: location }} replace />;
  }

  // Check role permissions if specified
  if (allowedRoles && !allowedRoles.includes(role)) {
    // Redirect based on user role
    const roleRedirects: Record<UserRole, string> = {
      admin: '/admin',
      teacher: '/teacher',
      student: '/student',
      parent: '/family',
      guest: '/',
    };

    return <Navigate to={roleRedirects[role] || '/'} replace />;
  }

  return <>{children}</>;
}

// HOC for route-level protection
export function withAuth<P extends object>(
  Component: React.ComponentType<P>,
  options?: { allowedRoles?: UserRole[]; redirectTo?: string }
) {
  return function AuthenticatedComponent(props: P) {
    return (
      <ProtectedRoute
        allowedRoles={options?.allowedRoles}
        redirectTo={options?.redirectTo}
      >
        <Component {...props} />
      </ProtectedRoute>
    );
  };
}
